/**
 * Markeroo Design System - Form Element Variants
 *
 * Official form element styles from the Figma design system.
 * See docs/design/forms.md for full specifications.
 *
 * Usage:
 *   import { inputVariants, selectVariants } from "@markeroo/theme/forms";
 *   <input className={inputVariants.default} />
 *   <select className={selectVariants.default}>...</select>
 */

/**
 * Base styles shared by all form inputs.
 * - 8px border radius (rounded-lg)
 * - Consistent border and background
 * - Focus ring for accessibility (uses focus-visible, not focus)
 * - Transition for smooth state changes
 */
const inputBase =
  "w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-body text-brand-950 shadow-sm transition focus-visible:border-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 disabled:cursor-not-allowed disabled:opacity-60";

/**
 * Input variants for text inputs, textareas, etc.
 */
export const inputVariants = {
  /**
   * Default Input - Standard text input
   * Background: White (surface-card)
   * Border: border color (gray)
   * Height: auto | Padding: 12px × 8px | Font: 15px
   */
  default: inputBase,
} as const;

/**
 * Select variants for dropdown/select elements.
 * Extends input base with:
 * - appearance-none to remove native styling
 * - Custom caret via background SVG (PiChevronDownStroke from Pikaicons)
 * - Extra right padding for the caret
 * - Cursor pointer for better UX
 */
export const selectVariants = {
  /**
   * Default Select - Standard dropdown with custom caret
   * Uses appearance-none with PiChevronDownStroke SVG as background.
   * Caret is positioned 12px from right edge.
   */
  default: `${inputBase} pr-10 cursor-pointer appearance-none bg-[length:20px_20px] bg-[right_0.75rem_center] bg-no-repeat [background-image:url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20fill%3D%27none%27%20viewBox%3D%270%200%2024%2024%27%3E%3Cpath%20stroke%3D%27%236b7280%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%20stroke-width%3D%272%27%20d%3D%27M8%2010.14a20.4%2020.4%200%200%200%203.702%203.893c.175.141.42.141.596%200A20.4%2020.4%200%200%200%2016%2010.14%27%2F%3E%3C%2Fsvg%3E")]`,
} as const;

/**
 * Textarea variant
 * Same as input but with vertical resize enabled
 */
export const textareaVariants = {
  default: `${inputBase} resize-y`,
} as const;

/** Type for input variant keys */
export type InputVariant = keyof typeof inputVariants;

/** Type for select variant keys */
export type SelectVariant = keyof typeof selectVariants;

/** Type for textarea variant keys */
export type TextareaVariant = keyof typeof textareaVariants;
